import React from 'react';
import { IconAlertTriangle } from '@tabler/icons-react';
import { usePropertyReadOnly } from '../hooks/usePropertyReadOnly';

interface PropertyInactiveBannerProps {
    /**
     * 'full' (default): "creating, editing, and deleting is disabled".
     * 'partial': "creating and editing is disabled" + a caller-supplied
     * note about what's still allowed (e.g. Room & Rates' delete carve-out).
     */
    variant?: 'full' | 'partial';
    partialNote?: string;
    className?: string;
}

/**
 * Persistent (non-dismissible) top-of-page notice shown on every read-only
 * page while the user's active property is inactive. Rendered PER GATED
 * PAGE, not globally in AdminLayout — unaffected modules (Property
 * Management, My Property, users/roles, billing, etc.) must look
 * completely normal.
 *
 * Not dismissible on purpose: this is a mode indicator, not a one-off
 * notification — dismissing it would strand staff in a UI that's silently
 * inert with no visible explanation.
 */
const PropertyInactiveBanner: React.FC<PropertyInactiveBannerProps> = ({ variant = 'full', partialNote, className }) => {
    const { isReadOnly, propertyName } = usePropertyReadOnly();

    if (!isReadOnly) {
        return null;
    }

    return (
        <div className={`alert alert-warning d-flex align-items-center mb-3 ${className ?? ''}`} role="alert">
            <IconAlertTriangle className="me-2 flex-shrink-0" />
            <div>
                <strong>{propertyName} is currently inactive.</strong>{' '}
                {variant === 'partial'
                    ? (partialNote ?? 'Creating and editing is disabled until the property is reactivated.')
                    : 'This module is view-only — creating, editing, and deleting is disabled until the property is reactivated.'}
            </div>
        </div>
    );
};

export default PropertyInactiveBanner;
